/**
 * EditableField.tsx
 * Feature: 116 - Entity Detail Modals
 * Author: Peter Dobler
 * Date: 2026-01-29
 * Constitution Version: 1.12.0
 *
 * Purpose: Display editable field with validation support for Control Plane properties.
 */

import React, { useId } from 'react';
import { Info } from 'lucide-react';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { Input, Textarea, Select, Toggle, AppTooltip } from '@/components/ui';

export interface EditableFieldProps<T extends FieldValues> {
  /** Field name for form control */
  name: Path<T>;
  /** Form control from react-hook-form */
  control: Control<T>;
  /** Field label */
  label: string;
  /** Field type */
  type?: 'text' | 'number' | 'select' | 'switch' | 'textarea';
  /** Whether field is required */
  required?: boolean;
  /** Optional tooltip text */
  tooltip?: string;
  /** Whether the field is loading */
  loading?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Options for select type */
  options?: Array<{ value: string | number; label: string }>;
  /** Minimum value for number type */
  min?: number;
  /** Maximum value for number type */
  max?: number;
  /** Input suffix (e.g., 'days') */
  suffix?: string;
  /** Number of rows for textarea */
  rows?: number;
  /** Whether field is disabled */
  disabled?: boolean;
  /** Validation rules */
  rules?: {
    required?: string | boolean;
    min?: { value: number; message: string };
    max?: { value: number; message: string };
    pattern?: { value: RegExp; message: string };
    validate?: (value: unknown) => string | boolean;
  };
}

/**
 * EditableField - Displays an editable field with react-hook-form integration
 */
export function EditableField<T extends FieldValues>({
  name,
  control,
  label,
  type = 'text',
  required = false,
  tooltip,
  loading = false,
  placeholder,
  options = [],
  min,
  max,
  suffix,
  rows = 3,
  disabled = false,
  rules = {},
}: EditableFieldProps<T>): React.ReactElement {
  // One id ties the visible label to its control, and the error to the control.
  const fieldId = useId();
  const errorId = `${fieldId}-error`;

  if (loading) {
    return (
      <div className="mb-4" aria-busy="true" data-testid="loading-skeleton">
        <div
          className="mb-1 h-4 animate-pulse rounded bg-[var(--color-bg-subtle)]"
          style={{ width: '30%' }}
        />
        <div className="h-10 animate-pulse rounded bg-[var(--color-bg-subtle)]" />
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center">
        <label
          htmlFor={type === 'switch' ? undefined : fieldId}
          className="text-xs font-medium text-[var(--color-text-secondary)]"
        >
          {label}
        </label>
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-xs text-[var(--color-status-danger)]">*</span>
        )}
        {tooltip && (
          <AppTooltip title={tooltip} placement="top">
            <Info className="ml-1 h-3.5 w-3.5 cursor-help text-[var(--color-text-secondary)]" />
          </AppTooltip>
        )}
      </div>

      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? rules.required || `${label} is required` : false,
          ...rules,
        }}
        render={({ field, fieldState: { error } }) => {
          // The error is rendered here, not through the kit's errorMessage, so it is
          // announced (role="alert") and described-by the control it belongs to.
          const errorText = error && (
            <p
              id={errorId}
              role="alert"
              className="mt-1.5 text-sm text-[var(--color-status-danger)]"
            >
              {error.message}
            </p>
          );
          const a11y = {
            id: fieldId,
            'aria-required': required || undefined,
            'aria-invalid': error ? true : undefined,
            'aria-describedby': error ? errorId : undefined,
          };

          // Switch field
          if (type === 'switch') {
            return (
              <div className="flex flex-col">
                <Toggle
                  checked={!!field.value}
                  onChange={(checked) => field.onChange(checked)}
                  disabled={disabled}
                  label={field.value ? 'Enabled' : 'Disabled'}
                  aria-label={label}
                />
                {errorText}
              </div>
            );
          }

          // Select field
          if (type === 'select') {
            return (
              <>
                <Select
                  {...field}
                  {...a11y}
                  value={field.value ?? ''}
                  selectSize="sm"
                  isFullWidth
                  error={!!error}
                  disabled={disabled}
                >
                  {placeholder && (
                    <option value="" disabled>
                      {placeholder}
                    </option>
                  )}
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                {errorText}
              </>
            );
          }

          // Textarea field
          if (type === 'textarea') {
            return (
              <>
                <Textarea
                  {...field}
                  {...a11y}
                  inputSize="sm"
                  error={!!error}
                  placeholder={placeholder}
                  disabled={disabled}
                  isFullWidth
                  rows={rows}
                />
                {errorText}
              </>
            );
          }

          // Text/Number field
          return (
            <>
              <Input
                {...field}
                {...a11y}
                type={type === 'number' ? 'number' : 'text'}
                inputSize="sm"
                error={!!error}
                placeholder={placeholder}
                disabled={disabled}
                isFullWidth
                min={type === 'number' ? min : undefined}
                max={type === 'number' ? max : undefined}
                rightAddon={suffix ? <span className="text-sm text-[var(--color-text-secondary)]">{suffix}</span> : undefined}
              />
              {errorText}
            </>
          );
        }}
      />
    </div>
  );
}

export default EditableField;
